/**
 * Opt-in encrypted workspace sync over Nostr (addressable kind).
 *
 * Privacy model:
 * - Requires an unlocked local vault (encryption intent + re-seal on import).
 * - Payload is plaintext JSON only after NIP-44 decrypt-to-self (signer).
 * - Event is tagged so only OpenDork queries/accepts it (kind + d + client/app).
 * - API keys are never included (buildWorkspaceExport strips them).
 *
 * Kind 39987 — OpenDork workspace snapshot (addressable, d = opendork-workspace-v1).
 */

import type { NostrEvent } from '@nostrify/nostrify';
import {
  decryptWithPassphrase,
  encryptWithPassphrase,
  isVaultUnlocked,
  type Nip44Signer,
} from '@/lib/crypto/vault';
import {
  isOpendorkSyncEvent,
  OPENDORK_CLIENT,
  OPENDORK_SYNC_D_TAG,
  OPENDORK_SYNC_KIND,
  opendorkSyncTags,
} from '@/lib/opendorkIdentity';
import { buildWorkspaceExport } from './export';
import { importWorkspaceFromJson } from './export';
import type { WorkspaceExport } from './types';

export { OPENDORK_SYNC_KIND, OPENDORK_SYNC_D_TAG };

export interface SyncPublishResult {
  eventId: string;
  createdAt: number;
}

export interface SyncStatus {
  lastPublishedAt?: number;
  lastPulledAt?: number;
  lastError?: string;
}

type SignEvent = (event: {
  kind: number;
  content: string;
  tags: string[][];
  created_at: number;
}) => Promise<NostrEvent>;

/**
 * Build NIP-44 sealed content for a sync event.
 * Vault must be unlocked so export is taken under encryption policy and
 * pull can re-seal into local IDB.
 */
export async function buildSyncContent(
  signer: Nip44Signer,
  pubkey: string,
): Promise<{ content: string; tags: string[][] }> {
  if (!signer.nip44?.encrypt) {
    throw new Error('Sync requires a signer with NIP-44 encryption');
  }
  if (!isVaultUnlocked()) {
    throw new Error(
      'Unlock local encryption before sync. Relay snapshots require an active vault so history stays sealed on this device after pull.',
    );
  }

  // Honor syncExcludeMessages from settings (buildWorkspaceExport reads it)
  const plain = await buildWorkspaceExport();
  // Strip non-portable fields; mark payload as OpenDork for pull validation.
  // providers[] is public config only (no API keys) — see buildWorkspaceExport.
  const payload: WorkspaceExport = {
    ...plain,
    app: OPENDORK_CLIENT,
    settings: {
      historyMode: plain.settings?.historyMode,
      defaultModelId: plain.settings?.defaultModelId,
      globalInstructions: plain.settings?.globalInstructions ?? '',
      syncExcludeMessages: plain.settings?.syncExcludeMessages,
      idleLockMinutes: plain.settings?.idleLockMinutes,
      encryptLocal: true,
      defaultPersonaId: plain.settings?.defaultPersonaId,
      groundedOnly: plain.settings?.groundedOnly,
    },
    providers: plain.providers ?? [],
    defaultAiProvider: plain.defaultAiProvider,
  };
  const json = JSON.stringify(payload);
  // Soft size guard — NIP-44 + large histories can choke relays/browsers
  if (json.length > 20 * 1024 * 1024) {
    throw new Error(
      'Workspace snapshot exceeds 20 MB. Enable “Exclude messages from sync” or delete old chats, then retry.',
    );
  }
  const content = await signer.nip44.encrypt(pubkey, json);

  return {
    content,
    tags: opendorkSyncTags(),
  };
}

export async function publishWorkspaceSync(opts: {
  signer: Nip44Signer & { signEvent: SignEvent };
  pubkey: string;
  publish: (event: NostrEvent) => Promise<void>;
}): Promise<SyncPublishResult> {
  const { content, tags } = await buildSyncContent(opts.signer, opts.pubkey);
  const created_at = Math.floor(Date.now() / 1000);
  const event = await opts.signer.signEvent({
    kind: OPENDORK_SYNC_KIND,
    content,
    tags,
    created_at,
  });
  await opts.publish(event);
  return { eventId: event.id, createdAt: created_at * 1000 };
}

export async function pullWorkspaceSync(opts: {
  signer: Nip44Signer;
  pubkey: string;
  query: (filters: {
    kinds: number[];
    authors: string[];
    '#d': string[];
    limit: number;
  }[]) => Promise<NostrEvent[]>;
  mode?: 'merge' | 'replace';
}): Promise<{
  imported: boolean;
  createdAt?: number;
  stats?: {
    projects: number;
    chats: number;
    messages: number;
    approxBytes: number;
  };
}> {
  if (!opts.signer.nip44?.decrypt) {
    throw new Error('Sync requires a signer with NIP-44 decryption');
  }
  if (!isVaultUnlocked()) {
    throw new Error(
      'Unlock local encryption before pull. Imported history is re-sealed with your vault key.',
    );
  }

  // Query with and without #d — some relays are picky about tag filters on
  // addressable kinds; we still validate d/client/app tags client-side.
  let events = await opts.query([
    {
      kinds: [OPENDORK_SYNC_KIND],
      authors: [opts.pubkey],
      '#d': [OPENDORK_SYNC_D_TAG],
      limit: 5,
    },
  ]);

  if (!events.length) {
    events = await opts.query([
      {
        kinds: [OPENDORK_SYNC_KIND],
        authors: [opts.pubkey],
        limit: 10,
      },
    ]);
  }

  if (!events.length) {
    return { imported: false };
  }

  // Newest first; skip events that fail OpenDork tag checks
  const sorted = [...events].sort((a, b) => b.created_at - a.created_at);
  const event = sorted.find((e) => isOpendorkSyncEvent(e.tags));
  if (!event) {
    // Had kind 39987 but none tagged for OpenDork
    const anyTagged = sorted.some((e) =>
      e.tags.some((t) => t[0] === 'd' && t[1] === OPENDORK_SYNC_D_TAG),
    );
    if (anyTagged) {
      throw new Error(
        'Remote snapshot found but missing OpenDork client/app tags — refused for safety',
      );
    }
    return { imported: false };
  }

  const json = await opts.signer.nip44.decrypt(opts.pubkey, event.content);
  let data: unknown;
  try {
    data = JSON.parse(json) as unknown;
  } catch {
    throw new Error('Remote snapshot decrypted but is not valid JSON');
  }

  // Validate portable payload belongs to OpenDork
  if (
    typeof data !== 'object' ||
    data === null ||
    (data as { app?: string }).app !== OPENDORK_CLIENT
  ) {
    throw new Error('Remote snapshot is not an OpenDork workspace — refused');
  }

  // Structural + size limits before IDB write
  const { validateWorkspaceExport } = await import('./validate');
  const byteLength = new TextEncoder().encode(json).length;
  const validated = validateWorkspaceExport(data, { jsonByteLength: byteLength });

  await importWorkspaceFromJson(validated, opts.mode ?? 'replace');
  return {
    imported: true,
    createdAt: event.created_at * 1000,
    stats: {
      projects: validated.projects.length,
      chats: validated.chats.length,
      messages: validated.messages.length,
      approxBytes: byteLength,
    },
  };
}

/**
 * Optional: double-wrap with passphrase for export-compatible sync blobs
 * (not used for Nostr publish — kept for future hybrid).
 */
export async function sealExportForSync(
  passphrase: string,
): Promise<{ salt: string; iv: string; ct: string }> {
  const plain = await buildWorkspaceExport();
  return encryptWithPassphrase(JSON.stringify(plain), passphrase);
}

export async function openExportFromSync(
  bundle: { salt: string; iv: string; ct: string },
  passphrase: string,
): Promise<WorkspaceExport> {
  const json = await decryptWithPassphrase(bundle, passphrase);
  return JSON.parse(json) as WorkspaceExport;
}
